/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable sonarjs/cognitive-complexity */

import { AnchorProvider } from "@coral-xyz/anchor";
import { MNDE_REALM } from "@marinade.finance/escrow-relocker-sdk/src";
import {
  MNDE_MINT,
  MNDE_VSR_PROGRAM_ID,
} from "@marinade.finance/incentives-distribution-sdk";
import { getVsrDepositAmount, getVsrProgram } from "@marinade.finance/vsr-sdk";
import { captureException } from "@sentry/nextjs";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import { useSearchParams } from "next/navigation";
import type { ReactNode } from "react";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { coinSymbols } from "../services/domain/coinSymbols";
import { coinTokens } from "../services/domain/coinTokens";
import { useMarinadeState } from "hooks/useMarinadeState";
import { useUsersVotingPower } from "hooks/useUsersVotingPower";
import type { TokenAccount, WalletInfo } from "services/domain/walletInfo";
import { findTokenAccountsByOwner } from "solana/find-token-accounts-by-owner";

const WalletInfoContext = createContext<WalletInfo>({
  walletAddress: null,
  simulation: false,
  SOLBalance: null,
  veMNDEBalance: null,
  tokenAccounts: new Map(),
  lpSOLPart: null,
  lpMSOLPart: null,
  transactionSigned: false,
  isLoaded: false,
  setTransactionSigned: () => {},
});

type WalletInfoProviderProps = { children: ReactNode };
type ConnectionSubscription = { key: PublicKey; subscription: number };

export function WalletInfoProvider({ children }: WalletInfoProviderProps) {
  const { connection } = useConnection();
  const { publicKey, wallet, signTransaction, signAllTransactions } =
    useWallet();
  const searchParams = useSearchParams();

  const [walletAddress, setWalletAddress] = useState<PublicKey | null>(null);
  const [simulation, setSimulation] = useState<boolean>(false);
  const [transactionSigned, setTransactionSigned] = useState<boolean>(false);

  const marinadeState = useMarinadeState();
  const { data: veMNDESnapshotBalance } = useUsersVotingPower(
    walletAddress?.toString()
  );
  const [SOLBalance, setSOLBalance] = useState<null | number>(null);
  const [veMNDEBalance, setVeMNDEBalance] = useState<null | number>(null);
  const [tokenAccounts, setTokenAccounts] = useState<Map<string, TokenAccount>>(
    new Map()
  );

  const [loaded, setLoaded] = useState({
    sol: false,
    tokens: false,
  });

  useEffect(() => {
    if (publicKey) {
      if (publicKey !== walletAddress) {
        setSimulation(false);
        setWalletAddress(publicKey);
      }
      return;
    }

    if (wallet?.adapter.name === "Sherlock Wallet") {
      setSimulation(true);
      setWalletAddress(publicKey);
      return;
    }

    if (searchParams) {
      const simulationWallet = searchParams.get("wallet");
      if (
        simulationWallet &&
        walletAddress !== new PublicKey(simulationWallet)
      ) {
        setSimulation(true);
        setWalletAddress(new PublicKey(simulationWallet));
        return;
      }
    }

    setWalletAddress(null);
    setLoaded({ sol: false, tokens: false });
  }, [publicKey, searchParams]);

  const SOLAccSubRef = useRef<undefined | ConnectionSubscription>();
  const tokenSubscriptionsRef = useRef<{
    [key: string]: { key: PublicKey; subscription: number };
  }>({});

  useEffect(() => {
    if (walletAddress) {
      const fetchBalance = async () => {
        const balance = await connection.getBalance(walletAddress);
        if (SOLAccSubRef.current) {
          setSOLBalance(Number(balance.toFixed(9)));
          setLoaded((cur) => ({ ...cur, sol: true }));
        }
      };
      fetchBalance();
      if (!SOLAccSubRef.current) {
        SOLAccSubRef.current = {
          key: walletAddress,
          subscription: connection.onAccountChange(walletAddress, (acc) => {
            setSOLBalance(acc.lamports);
          }),
        };
      }
    }
    return () => {
      if (SOLAccSubRef.current) {
        connection.removeAccountChangeListener(
          SOLAccSubRef.current.subscription
        );
        SOLAccSubRef.current = undefined;
      }
    };
  }, [connection, walletAddress, SOLBalance]);

  const fetchBalances = async () => {
    if (!walletAddress) return;

    const accounts = await findTokenAccountsByOwner(connection, walletAddress);

    Promise.all<{ key: string; token: TokenAccount } | undefined>(
      accounts.map(async (account) => {
        try {
          const { uiAmount } = account.account.data.parsed.info.tokenAmount;
          const { mint } = account.account.data.parsed.info;

          const coinSymbol = Object.keys(coinTokens).find(
            (symbol) =>
              coinTokens[symbol].fetchBalance &&
              coinTokens[symbol].address === mint
          );
          if (!coinSymbol) return undefined;

          return {
            key: coinSymbol,
            token: {
              address: account.pubkey,
              balance: uiAmount,
              balanceInSol: 0,
              priceInSol: 0,
              tenEpochAPY: 0,
            },
          };
        } catch (ex) {
          captureException(ex);
          return undefined;
        }
      })
    ).then((parsedAccounts) => {
      const parsedAccountsMap = parsedAccounts
        .filter((account) => account)
        .sort((a, b) => (b?.token?.balance || 0) - (a?.token?.balance || 0))
        .reduce((map, account) => {
          if (account && !map.has(account.key)) {
            map.set(account.key, account.token);
          }
          return map;
        }, new Map<string, TokenAccount>());
      setTokenAccounts(parsedAccountsMap);
      setLoaded((cur) => ({ ...cur, tokens: true }));
    });
  };

  useEffect(() => {
    if (marinadeState?.mSolPrice.sol) {
      fetchBalances();
    }
  }, [walletAddress]);

  useEffect(() => {
    tokenAccounts.forEach((acc, key) => {
      if (!tokenSubscriptionsRef.current[key]) {
        tokenSubscriptionsRef.current[key] = {
          key: acc.address,
          subscription: connection.onAccountChange(acc.address, fetchBalances),
        };
      }
    });

    return () => {
      Object.keys(tokenSubscriptionsRef.current).forEach((symbol) => {
        connection.removeAccountChangeListener(
          tokenSubscriptionsRef.current[symbol].subscription
        );
        delete tokenSubscriptionsRef.current[symbol];
      });
    };
  }, [tokenAccounts]);

  const [lpSOLPart, lpMSOLPart] = useMemo(() => {
    const lpBalance = tokenAccounts.get(coinSymbols.lpSOL)?.balance;
    if (!lpBalance) {
      return [0, 0];
    }
    const solPart = marinadeState
      ? (lpBalance * marinadeState.liquidityPool.solBalance.sol) /
        marinadeState.liquidityPool.supply.sol
      : 0;
    const msolPart = marinadeState
      ? (lpBalance * marinadeState.liquidityPool.mSolBalance.sol) /
        marinadeState.liquidityPool.supply.sol
      : 0;

    return [solPart, msolPart];
  }, [tokenAccounts, marinadeState]);

  const walletInfoValue = useMemo(() => {
    return {
      walletAddress,
      simulation,
      SOLBalance,
      veMNDEBalance: veMNDEBalance ?? null,
      tokenAccounts,
      lpSOLPart,
      lpMSOLPart,
      transactionSigned,
      isLoaded: Object.values(loaded).every((i) => i),
      setTransactionSigned,
    };
  }, [
    SOLBalance,
    veMNDEBalance,
    tokenAccounts,
    lpSOLPart,
    lpMSOLPart,
    transactionSigned,
    loaded,
    setTransactionSigned,
  ]);

  const getLockedMNDE = async () => {
    if (!wallet || !signTransaction || !signAllTransactions || !publicKey)
      return;
    const vsrProgram = getVsrProgram({
      connection,
      provider: new AnchorProvider(
        connection,
        {
          signTransaction,
          signAllTransactions,
          publicKey,
        },
        {}
      ),
      programId: MNDE_VSR_PROGRAM_ID,
    });
    try {
      const lamportAmount = await getVsrDepositAmount({
        vsrProgram,
        mint: MNDE_MINT,
        realm: MNDE_REALM,
        authority: publicKey,
      });
      const amount = lamportAmount.toNumber() / LAMPORTS_PER_SOL;
      if (!veMNDESnapshotBalance) {
        setVeMNDEBalance(amount);
      } else {
        setVeMNDEBalance(veMNDESnapshotBalance);
      }
    } catch {
      // Do nothing, wallet does not have any locked MNDE
    }
  };

  useEffect(() => {
    if (wallet && connection) {
      getLockedMNDE();
    }
  }, [wallet, veMNDESnapshotBalance, connection]);

  return (
    <WalletInfoContext.Provider value={walletInfoValue}>
      {children}
    </WalletInfoContext.Provider>
  );
}

export function useWalletInfo(): WalletInfo {
  return useContext(WalletInfoContext);
}
